import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ApiService from '../../core/services/network.service';
import * as endpoints from '../../constants/endpoints';
import companyImage from '../../assets/icons/avatar-placeholder.svg';
import mapPin from '../../assets/icons/map-pin-black.svg';
import Vacancy from '../../models/vacancy.interface';
import { salaryRange } from '../../utils/format-salary';
import './CompanyLocation.scss';

interface CandidateCompany {
  id: string;
  name: string;
  logo?: string;
  cover?: string;
  url?: string;
  description?: string;
  address?: string;
  city?: string;
  country?: string;
  sector?: string;
}

const CompanyLocation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [company, setCompany] = useState<CandidateCompany | null>(null);
  const [offers, setOffers] = useState<Vacancy[]>([]);
  const requestHeaders = t('candidateRequestPage.tableHeaders', {
    returnObjects: true,
  }) as string[];

  useEffect(() => {
    if (!companyId) return;
    let isMounted = true;

    ApiService.request<{ body?: CandidateCompany }>(
      'GET',
      endpoints.COMPANY_DETAIL.replace('{id}', companyId)
    ).then((res) => {
      if (!isMounted) return;
      setCompany(res?.body || null);
    });

    async function fetchCompanyOffers() {
      if (!companyId) return;

      const requestPage = (page: number) =>
        ApiService.request<{
          body?: {
            data?: Vacancy[];
            totalPages?: number;
          };
        }>('GET', endpoints.OFFERS_COMPANY.replace('{id}', companyId), null, false, {
          params: {
            page,
            size: 100,
            status: 2,
          },
        });

      const firstPage = await requestPage(1);
      const totalPages = firstPage?.body?.totalPages || 1;
      const remainingPages =
        totalPages > 1
          ? await Promise.all(
              Array.from({ length: totalPages - 1 }, (_, index) =>
                requestPage(index + 2)
              )
            )
          : [];

      if (!isMounted) return;

      setOffers(
        [firstPage, ...remainingPages].flatMap(
          (response) => response?.body?.data || []
        )
      );
    }

    fetchCompanyOffers();

    return () => {
      isMounted = false;
    };
  }, [companyId]);

  if (!company) return null;

  const location = [company.address, company.city, company.country]
    .filter(Boolean)
    .join(', ');

  return (
    <div className='location'>
      <div className='preview position-relative'>
        {company.cover ? (
          <img
            src={company.cover}
            alt='preview'
            className='w-100 preview__image'
          />
        ) : (
          <div className='w-100 preview__image'></div>
        )}
        <div className='preview__general d-flex justify-content-between'>
          <div className='position-relative d-flex gap-3 align-items-center'>
            <div className='preview__logo-container position-absolute object-fit-cover rounded-circle'>
              <img
                src={company.logo || companyImage}
                alt='logo'
                className='preview__logo object-fit-cover rounded-circle'
              />
            </div>
            <div className='preview__title d-flex flex-column gap-1'>
              <p>{company.name}</p>
              <p>{company.sector}</p>
            </div>
          </div>
        </div>
      </div>

      <div className='preview location__info position-relative'>
        <div className='d-flex flex-column gap-4 mb-4'>
          {company.url && (
            <div className='d-flex gap-4'>
              <p className='fw-bold'>URL</p>
              <p>{company.url}</p>
            </div>
          )}
          {location && (
            <div className='d-flex gap-4'>
              <p className='fw-bold'>{t('vacancyLocationPage.location')}</p>
              <p>{location}</p>
            </div>
          )}
        </div>

        {company.description && (
          <div className='mt-4'>
            <p className='fw-bold mb-3'>
              {t('vacancyLocationPage.description')}
            </p>
            <p className='breaklines'>{company.description}</p>
          </div>
        )}
      </div>

      {offers.length > 0 && (
        <div className='preview location__info position-relative'>
          <h4>{t('vacancyLocationPage.activeVacancies')}</h4>

          <table className='request-table w-100 mobile-hidden'>
            <thead>
              <tr>
                <th>{requestHeaders[1]}</th>
                <th>{requestHeaders[2]}</th>
                <th>{requestHeaders[3]}</th>
                <th>{t('vacanciesTablet.columns.endDate')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {offers.map((offer) => (
                <tr key={offer.id}>
                  <td>
                    <p>{offer.position}</p>
                  </td>
                  <td>
                    <div className='d-flex align-items-center gap-2'>
                      <img
                        width={20}
                        height={20}
                        src={mapPin}
                        alt='location'
                      />
                      <p>{offer.location}</p>
                    </div>
                  </td>
                  <td>
                    <p className='font-medium'>
                      {salaryRange(
                        +offer.salary_min,
                        +offer.salary_max,
                        t('salary.minPrefix'),
                        t('salary.maxPrefix')
                      )}
                    </p>
                  </td>
                  <td>
                    <p className='font-medium'>{offer.end_date}</p>
                  </td>
                  <td>
                    <Button onClick={() => navigate(`/candidate/${offer.id}`)}>
                      {t('candidateRequestPage.navigationButton')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className='company-offers-mobile no-mobile-hidden flex-column gap-2'>
            {offers.map((offer) => (
              <div
                key={offer.id}
                className='list-item-wrapper d-flex justify-content-between align-items-center'
              >
                <div>
                  <p>{offer.position}</p>
                  <p className='font-medium'>{offer.end_date}</p>
                </div>
                <Button onClick={() => navigate(`/candidate/${offer.id}`)}>
                  {t('candidateRequestPage.mobileNavigation')}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyLocation;
